import { useCallback, useEffect, useRef, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { ExplainedButton as Button } from '../explained-button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { CourseLoyaltyDto, ListCourseDto } from '../../general/dtos';
import { DiscountDraftAction, discountKey, mergeDiscount, loyaltyDraftProblem } from '../../general/course-discount-draft';

const emptyRule = (): CourseLoyaltyDto => ({ target_course_key: '', discount_percent: '10.00' });

export function CourseLoyalty({ courseKey, courseName, courses, rules, onChange, onDraftChange, busy = false }: {
    courseKey: string; courseName: string; courses: ListCourseDto[]; rules: CourseLoyaltyDto[];
    onChange: (rules: CourseLoyaltyDto[]) => void; onDraftChange: (action: DiscountDraftAction<CourseLoyaltyDto> | null) => void; busy?: boolean;
}) {
    const [editing, setEditing] = useState<CourseLoyaltyDto>(emptyRule);
    const [error, setError] = useState('');
    const baseline = useRef<CourseLoyaltyDto>(emptyRule());
    const reset = useCallback((value = emptyRule()) => { baseline.current = value; setEditing(value); setError(''); }, []);
    const dirty = JSON.stringify(editing) !== JSON.stringify(baseline.current);
    const merge = useCallback(() => {
        const problem = loyaltyDraftProblem(editing, rules, courseKey);
        if (problem) {
            setError(problem.message);
            requestAnimationFrame(() => document.getElementById(problem.field)?.focus());
            return null;
        }
        return mergeDiscount(rules, { ...editing, target_course_name: courses.find(course => course.course_key === editing.target_course_key)?.name });
    }, [editing, rules, courseKey, courses]);
    const collect = useCallback(() => dirty ? merge() : rules, [dirty, merge, rules]);
    useEffect(() => {
        onDraftChange({ collect, dirty, discard: reset });
        return () => onDraftChange(null);
    }, [onDraftChange, collect, dirty, reset]);
    const options = courses.filter(course => course.course_key !== courseKey &&
        (!rules.some(rule => rule.target_course_key === course.course_key && discountKey(rule) !== discountKey(editing))));
    const percent = Number(editing.discount_percent);
    const problem = loyaltyDraftProblem(editing, rules, courseKey);
    const disabled = busy || !!problem;
    const rowBlocked = busy ? 'Wait for the course save to finish.' : 'Add or update the current entry, or clear the form before editing another discount.';
    const stage = () => { const next = merge(); if (next) { onChange(next); reset(); } };
    return <div className="flex flex-column gap-3">
        <h3 className="m-0">Loyalty discounts</h3>
        <p className="m-0">Purchasing {courseName} unlocks these discounts on other courses. Select the course to discount and its percentage.</p>
        <p className="m-0">The highest eligible loyalty discount is added to the promo-code percentage. Combined discounts must be below 100%.</p>
        <small>Add, update or remove entries in this draft. All changes take effect together after Save, for new checkouts.</small>
        {error && <p role="alert">{error}</p>}
        <DataTable value={rules} dataKey="target_course_key" loading={busy} emptyMessage="No loyalty discounts for this course.">
            <Column field="target_course_name" header="Discounted course" />
            <Column field="discount_percent" header="Discount (%)" />
            <Column body={(rule: CourseLoyaltyDto) => <div className="flex flex-wrap gap-2">
                <Button label="Edit" text disabled={busy || dirty} disabledReason={rowBlocked} onClick={() => reset({ ...rule })} />
                <Button label="Remove" text severity="danger" disabled={busy || dirty} disabledReason={rowBlocked} onClick={() => { onChange(rules.filter(value => discountKey(value) !== discountKey(rule))); reset(); }} />
            </div>} />
        </DataTable>
        <h3 className="mb-0">{discountKey(editing) ? 'Edit loyalty discount' : 'New loyalty discount'}</h3>
        <label htmlFor="loyalty-target">Discounted course</label>
        <Dropdown inputId="loyalty-target" value={editing.target_course_key} options={options} optionLabel="name" optionValue="course_key"
            filter placeholder="Select another course" disabled={busy}
            onChange={event => setEditing({ ...editing, target_course_key: event.value })} />
        <label htmlFor="loyalty-percent">Discount (%)</label>
        <InputNumber inputId="loyalty-percent" value={percent} min={0.01} max={99.99} maxFractionDigits={2} disabled={busy}
            onValueChange={event => setEditing({ ...editing, discount_percent: (event.value ?? 0).toFixed(2) })} />
        <div className="admin-toolbar flex flex-wrap gap-2">
            <Button label={discountKey(editing) ? 'Update entry' : 'Add to course'} disabled={disabled}
                disabledReason={busy ? 'Wait for the course save to finish.' : problem?.message} onClick={stage} />
            <Button label="Clear form" outlined disabled={busy} disabledReason="Wait for the course save to finish." onClick={() => reset()} />
        </div>
    </div>;
}
